import React, { useState, useRef, useEffect, useContext } from "react";
import { Mic, Upload, X } from "lucide-react";
import { UserContext } from "../../../../../../UserContext";
import { FaSquare, FaPlay, FaPause } from "react-icons/fa";
import Cropper from "react-easy-crop";

const CurrentYouForm = ({ formData, 
  subStep, 
  essentialsField, 
  setEssentialsField, 
  uploading, 
  onInputChange, 
  onImageUpload, 
  onVoiceUpload,
  onEssentialsNext  }) => {
  // Local micro-navigation within Essentials
  // const [essentialsField, setEssentialsField] = useState(0);

  // useEffect(() => {
  //   if (subStep === 1) setEssentialsField(0);
  // }, [subStep]);
  
  const { user } = useContext(UserContext);
  
  const userName =
    user?.firstName
      ? `${user.firstName} `
      : user?.firstName || "";

  // Image cropping states
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);

  // Redo confirmation state
  const [showRedoConfirm, setShowRedoConfirm] = useState(false);

  const essentialsQuestions = [
    { id: "currentTitle", label: "What's your current job title?", example: "Eg. Senior Manager (Client Servicing) or AVP - Special Projects" },
    { id: "company", label: "Which organization are you contributing to?" , example: "Eg. Infosys, JP Morgan, Ogilvy, etc."},
    { id: "location", label: "Which city do you call home these days?", example: "Eg. Delhi, Pune etc." },
    { id: "salary", label: "Could you tell me your current salary range?", type: "options" },
    { id: "totalExperience", label: "experience do you have in total?", example: "Eg. 4 Years 2 month etc.", type: "experience"  },
  ];

  const salaryOptions = [
    { value: "5-10LPA", label: "5-10LPA" },
    { value: "10-20LPA", label: "10-20LPA" },
    { value: "20-50LPA", label: "20-50LPA" },
    { value: "more-than-50LPA", label: "More than 50LPA" },
  ];

  const experienceYears = Array.from({ length: 31 }, (_, i) => ({
    value: i,
    label: `${i} Year${i !== 1 ? 's' : ''}`
  }));

  const experienceMonths = Array.from({ length: 12 }, (_, i) => ({
    value: i,
    label: `${i} Month${i !== 1 ? 's' : ''}`
  }));

  const essentialsPrompts = [
    `Awesome ${userName}! Nice to see you.`,
    // `Good to know that you're a ${formData.currentTitle || ""}.`,
    `Nice, ${formData.currentTitle || ""}'s a dynamic space.`,
    // "Great to know where you're working! I'm curious...",
    `${formData.company}...very interesting!`,
    // `${formData.location || "Your city"} is the place to be.`,
    "That helps put things in perspective. One other thing:",
    // "Got it. Finally, just to complete the picture",
    "Got it. And how many years of work"
  ];

  // Voice note recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentPlayTime, setCurrentPlayTime] = useState(0);
  const [audioURL, setAudioURL] = useState(formData.futureVoiceNote || null);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!audioRef.current || !audioURL) return;

    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      setAudioDuration(Math.floor(audio.duration || 0));
    };

    const handleTimeUpdate = () => {
      setCurrentPlayTime(Math.floor(audio.currentTime));
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [audioURL]);

  useEffect(() => {
    // Keep preview in sync if parent updates formData.futureVoiceNote
    setAudioURL(formData.futureVoiceNote || null);
  }, [formData.futureVoiceNote]);

  // Image cropping functions
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/jpeg");
    });
  };

  const handleCropSave = async () => {
    try {
      const croppedImageBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
      const croppedImageFile = new File([croppedImageBlob], "cropped-image.jpg", {
        type: "image/jpeg",
      });

      // Create a synthetic event object
      const syntheticEvent = {
        target: {
          files: [croppedImageFile],
        },
      };

      // Call the original onImageUpload with the cropped image
      await onImageUpload(syntheticEvent);
      
      setShowCropModal(false);
      setImageToCrop(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageToCrop(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        await onVoiceUpload?.(audioBlob);
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((p) => p + 1), 1000);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const redoRecording = () => {
    setShowRedoConfirm(true);
  };

  const confirmRedo = () => {
    setAudioURL(null);
    setRecordingTime(0);
    setAudioDuration(0);
    setCurrentPlayTime(0);
    setIsPlaying(false);
    onInputChange?.("futureVoiceNote", null);
    setShowRedoConfirm(false);
  };

  const cancelRedo = () => {
    setShowRedoConfirm(false);
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // const goNextField = async () => {
  //   if (essentialsField < essentialsQuestions.length - 1) {
  //     if (onEssentialsNext) {
  //       await onEssentialsNext();
  //     }
  //   }
  // };

  // Render helpers for each section
  const renderEssentials = () => {
    if (!formData.profileImage) {
      return (
        <div>
          <p className="font-bold text-lg md:text-[18px] text-[#323232] font-gotham mb-2">Let's create a card with your present identity.</p>
          <p className="text-sm md:text-[15px] font-gotham font-normal text-[#888888] mb-6">Upload / drag your image in the box below.</p>
          <label className="block cursor-pointer">
            <input 
              type="file" 
              accept="image/*" 
              onChange={handleImageSelect} 
              disabled={uploading} 
              className="hidden" 
            />
    
            <div 
              className="rounded-lg h-48 flex flex-col items-center justify-center hover:bg-gray-50 transition-all relative"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23BDBDBD' stroke-width='5' stroke-dasharray='8%2c 16' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
              }}
            >
              {uploading ? (
                <>
                  <div className="w-12 h-12 border-4 border-[#54B0AF] border-t-transparent rounded-full animate-spin mb-3"></div>
                  <p className="text-sm text-teal-600 font-medium">Uploading to cloud...</p>
                </>
              ) : formData.profileImage ? (
                <div className="flex flex-col items-center">
                  <img 
                    src={formData.profileImage} 
                    alt="Profile" 
                    className="w-32 h-32 rounded-full object-cover mb-3 border-4 border-[#54B0AF]"
                  />
                  <p className="text-xs text-gray-400 mt-1">Or click to change image</p>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-gray-400 mb-3" />
                  <p className="text-sm text-gray-500">Click to upload image</p>
                  <p className="text-xs text-gray-400 mt-2">Max 5MB</p>
                </>
              )}
            </div>
          </label>
        </div>
      );
    }

    // Show one question at a time; advance on Enter or salary click
    const currentQuestion = essentialsQuestions[essentialsField];

    // const goNextField = () => {
    //   if (essentialsField < essentialsQuestions.length - 1) {
    //     setEssentialsField((f) => f + 1);
    //   }
    // };

const goNextField = async () => {
  if (essentialsField < essentialsQuestions.length - 1) {
    if (onEssentialsNext) {
      await onEssentialsNext();
    }
  }
};

    return (
      <div>
        <p className="font-bold font-gotham text-sm md:text-[18px] text-[#323232] mb-2">{essentialsPrompts[essentialsField]}</p>
        <p className="text-sm md:text-[18px] text-[#323232] font-gotham font-bold mb-2">{currentQuestion.label}</p>

        {currentQuestion.example && (
          <p className="text-xs md:text-[14px] text-[#888888] font-gotham font-normal mb-6 md:mb-8">
            {currentQuestion.example}
          </p>
        )}

        {currentQuestion.type === "experience" ? (
          <div className="flex gap-3 md:gap-4">
            <div className="flex">
              <select
                value={formData.experienceYears || ""}
                onChange={(e) => onInputChange("experienceYears", e.target.value)}
                className={`w-full px-4 py-2 md:py-2.5 border-2 rounded-[10px] 
                  text-base md:text-[20px] font-gotham appearance-none cursor-pointer
                  outline-none
                  ${
                    formData.experienceYears
                      ? "bg-white text-[#444444] border-[#54B0AF]"
                      : "bg-[#F8F8F8] text-[#9A9A9A] border-[#CCCCCC]"
                  }
                `}
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 1rem center",
                  paddingRight: "3rem",
                }}
              >
                <option value="" disabled>Years</option>
                {experienceYears.map(year => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex">
              <select
                value={formData.experienceMonths || ""}
                onChange={(e) => onInputChange("experienceMonths", e.target.value)}
              // className="w-full px-4 py-3 md:py-4 border-2 border-[#E3E3E3] rounded-lg font-gotham font-medium text-base md:text-lg focus:border-[#54B0AF] outline-none bg-white text-gray-700 appearance-none cursor-pointer"
                className="w-full px-4 py-2 md:py-2.5 border-2 border-[#CCCCCC] rounded-[10px] text-base focus:border-[#54B0AF] outline-none bg-[#F8F8F8] text-[#444444] font-gotham font-normal md:text-[20px] appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 1rem center',
                  paddingRight: '3rem'
                }}
              >
              {/* <option value="">Months</option>
              {experienceMonths.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option> */}
                <option value="" disabled>Months</option>
                {experienceMonths.map(month => (
                  <option 
                    key={month.value} 
                    value={month.value}
                    className="bg-white text-[#444444] hover:bg-[#54B0AF] hover:text-white border border-[#CCCCCC] rounded-[10px]"
                    style={{
                      backgroundColor: formData.experienceMonths === month.value ? '#54B0AF' : 'white',
                      color: formData.experienceMonths === month.value ? 'white' : '#444444',
                      padding: '8px 12px'
                    }}
                  >
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : currentQuestion.type === "options" ? (
          <div className="space-y-3 flex flex-col">
            {salaryOptions.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onInputChange("salary", option.value);
                  goNextField();
                }}
                className={`max-w-xs text-left px-5 py-3 rounded-[50px] border-2 transition-all ${
                  formData.salary === option.value
                    ? "bg-[#54B0AF] text-white border-[#CCCCCC]"
                    : "bg-[#F8F8F8] text-gray-700 border-[#CCCCCC] hover:border-[#54B0AF]"
                }`}
              >
                <div className="flex justify-start items-center">
                  <span className="w-6 h-6 md:h-7 border border-[#CCCCCC] md:w-7 rounded-full bg-white flex items-center justify-center mr-3 flex-shrink-0">
                    <span className={`font-semibold text-xs ${formData.salary === option.value ? "text-black" : "text-[#1C1C1C]"}`}>{String.fromCharCode(65 + index)}</span>
                  </span>
                  <span className={`font-medium text-sm lg:text-[20px] font-gotham mt-2.5${formData.salary === option.value ? "text-white" : "text-[#444444]"}`}>{option.label}</span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <>
            <input
              type="text"
              value={formData[currentQuestion.id] || ""}
              onChange={(e) => onInputChange(currentQuestion.id, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.target.value || "").trim()) {
                  goNextField();
                }
              }}
              placeholder="Type here..."
              className={`w-full border-b-2 font-gotham font-medium text-lg md:text-lg ${formData[currentQuestion.id] ? "border-[#54B0AF]" : "border-[#E3E3E3]"} focus:border-[#54B0AF] outline-none py-3 ${formData[currentQuestion.id] ? "text-[#54B0AF]" : "text-gray-900"} placeholder-gray-400 transition-colors font-medium`}
            /> 
            <p className="text-xs md:text-[15px] font-gotham font-normal text-[#888888] mt-2">Press <span className="text-[#888888] font-bold">Enter</span> <span className="text-[#888888] mt-0.5 font-bold">↵</span></p>
          </>
        )}
      </div>
    );
  };

  const renderKnownFor = () => (
    <div>
      <p className="font-bold text-[#323232] font-gotham text-base lg:text-[18px] mb-2">Good to know you better. Now let's shift focus: what are four things your colleagues or friends would say you're known for?</p>
      <p className="text-sm font-gotham md:text-[15px] text-[#888888] mb-4">Eg. Team Management, Handling Conflicts, Automation Implementation, etc.</p>
      <div className="space-y-3">
        {[
          { id: "technicalSkills", placeholder: "Technical skills you are good at" },
          { id: "behaviors", placeholder: "Behaviours you're admired for" },
          { id: "advice", placeholder: "Things people come to you for advice" },
          { id: "hobbies", placeholder: "Hobbies outside of work you love doing" },
        ].map((q) => (
          <div key={q.id} className="relative w-full">
            <span className="absolute left-0 top-1/2 -translate-y-1/2 text-[#54B0AF]">+</span>
            <input
              type="text"
              value={formData[q.id] || ""}
              onChange={(e) => onInputChange(q.id, e.target.value)}
              placeholder={q.placeholder}
              className={`w-full border-b-2 font-gotham ${formData[q.id] ? "border-[#54B0AF]" : "border-[#B8B8B8]"} focus:border-[#54B0AF] text-[#54B0AF] text-base lg:text-lg outline-none py-3 pl-6 ${formData[q.id] ? "text-[#54B0AF]" : "text-gray-900"} placeholder-[#B8B8B8] font-medium transition-colors`}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderFutureNote = () => (
    <div>
      <p className="font-bold text-lg md:text-[18px] text-[#323232] font-gotham">As we wrap up, take a moment to look forward.</p>
      <p className="text-lg md:text-[18px] font-gotham font-bold text-[#323232] mb-6">What message would you like to leave for your future self?</p>

      {!audioURL && !isRecording && (
        <div 
          onClick={startRecording}
          className="border-2 border-dashed border-gray-300 rounded-lg h-48 flex flex-col items-center justify-center hover:border-teal-400 transition-all hover:bg-[#D3D0D0] cursor-pointer"
        >
          <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-4">
            <Mic className="w-8 h-8 text-white" />
          </div>
          <p className="text-sm text-gray-600 font-medium">Click to record voice note</p>
          <p className="text-xs text-gray-400 mt-2">Tap the microphone to start</p>
        </div>
      )}

      {isRecording && (
        <div className="border-2 border-teal-500 rounded-lg p-8 bg-teal-50">
          <div className="flex flex-col items-center justify-center">
            <div className="relative mb-6">
              <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center animate-pulse">
                <Mic className="w-10 h-10 text-white" />
              </div>
              <div className="absolute inset-0 w-20 h-20 bg-teal-500 rounded-full animate-ping opacity-20"></div>
            </div>
            
            <div className="mb-6">
              <p className="text-4xl font-bold text-teal-600 font-mono">{formatTime(recordingTime)}</p>
            </div>

            <button
              onClick={stopRecording}
              className="flex items-center gap-2 px-8 py-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all font-semibold"
            >
              <FaSquare className="w-5 h-5" />
              <span>Stop Recording</span>
            </button>
          </div>
        </div>
      )}

      {audioURL && !uploading && (
        <div className="border-2 border-gray-300 rounded-lg p-6 bg-[#F1F1F1] max-w-xl">
          <div className="flex items-center gap-4 mb-1">
            <button
              onClick={togglePlayPause}
              className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center hover:bg-teal-600 transition-all"
            >
              {isPlaying ? (
                <FaPause className="w-6 h-6 text-white" />
              ) : (
                <FaPlay className="w-6 h-6 text-white ml-1" />
              )}
            </button>
            <div className="flex-1">
              <div className="h-12 bg-[#F1F1F1] rounded-lg flex items-center px-4">
                <div className="flex gap-1 items-center justify-center w-full">
                  {Array.from({ length: 40 }).map((_, i) => (
                    <div key={i} className="w-1 bg-[#C4C1C1] rounded-full" style={{ height: `${Math.random() * 24 + 8}px`, opacity: isPlaying ? 0.8 : 0.4 }} />
                  ))}
                </div>
              </div>
            </div>
            {/* <span className="text-sm md:text-2xl text-[#666666] font-bold font-gotham  min-w-[40px]">{formatTime(recordingTime)}</span> */}
            <span className="text-sm md:text-2xl text-[#666666] font-bold font-gotham  min-w-[40px]">{formatTime(isPlaying ? currentPlayTime : audioDuration)}</span>
          </div>
          {/* <audio ref={audioRef} src={audioURL} onEnded={() => setIsPlaying(false)} className="hidden" /> */}
          <audio
            ref={audioRef}
            src={audioURL}
            preload="metadata"
            onLoadedMetadata={(e) => {
              const duration = Math.floor(e.currentTarget.duration || 0);
              setAudioDuration(duration);
            }}
            onTimeUpdate={(e) => {
              setCurrentPlayTime(Math.floor(e.currentTarget.currentTime));
            }}
            onEnded={() => {
              setIsPlaying(false);
              setCurrentPlayTime(0);
            }}
            className="hidden"
          />
        </div>
      )}

      {uploading && (
        <div className="border-2 border-gray-300 rounded-lg p-8 bg-gray-50">
          <div className="flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-sm text-teal-600 font-medium">Uploading to cloud...</p>
          </div>
        </div>
      )}

      <button
        onClick={redoRecording}
        className="w-full text-start text-sm md:text-[12px]  text-[#D3D0D0] hover:text-[#54B0AF] font-gotham font-bold uppercase tracking-wide mt-4"
      >
        REDO NOTE
      </button>

      {/* Redo Confirmation Modal */}
      {showRedoConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-gotham font-semibold text-[#323232] mb-4">
              Delete Recording?
            </h3>
            <p className="text-sm font-gotham text-[#666666] mb-6">
              Are you sure you want to delete your current voice note? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelRedo}
                className="px-6 py-2 border-2 border-gray-300 rounded-lg font-gotham font-medium text-[#323232] hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRedo}
                className="px-6 py-2 bg-red-500 text-white rounded-lg font-gotham font-medium hover:bg-red-600 transition-colors"
              >
                Delete & Redo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (subStep === 1) return (
    <>
      {renderEssentials()}
      
      {/* Image Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-xl font-gotham font-semibold">Crop Your Image</h3>
              <button
                onClick={handleCropCancel}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="relative h-96 bg-gray-100">
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1} // Change this to adjust aspect ratio: 1 = square, 4/3 = landscape, 3/4 = portrait
                cropShape="rect" // rounded for circle, rect for square
                showGrid={true} // shows grid lines
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-gotham mb-2">
                  Zoom: {zoom.toFixed(1)}x
                </label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #54B0AF 0%, #54B0AF ${
                      ((zoom - 1) / 2) * 100
                    }%, #E5E7EB ${((zoom - 1) / 2) * 100}%, #E5E7EB 100%)`,
                  }}
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCropCancel}
                  className="px-6 py-2 border-2 border-gray-300 rounded-lg font-gotham font-medium text-[#323232] hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCropSave}
                  className="px-6 py-2 bg-[#54B0AF] text-white rounded-lg font-gotham font-medium hover:bg-teal-600 transition-colors"
                >
                  Save & Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
  
  if (subStep === 2) return renderKnownFor();
  return renderFutureNote();
};

export default CurrentYouForm;